import { fetchWithAuth } from './authHelpers';

// Hook for all friend management functionality
export const useFriends = () => {
    // Check friendship status with another user
    const checkFriendshipStatus = async (userNumber) => {
        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/friends/status/${userNumber}`, 'GET');
            
            if (!response.ok) {
                if (response.status === 404) {
                    throw new Error('User not found');
                }
                throw new Error('Failed to check friendship status');
            }
            
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error checking friendship status');
        }
    };

    // Send friend request
    const sendFriendRequest = async (recipientId) => {
        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/friends/requests`, 'POST', {
                recipientId
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to send friend request');
            }
            
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error sending friend request');
        }
    };

    // Accept friend request
    const acceptFriendRequest = async (requesterId) => {
        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/friends/requests/${requesterId}/accept`, 'PATCH');
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to accept friend request');
            }
            
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error accepting friend request');
        }
    };

    // Decline friend request
    const declineFriendRequest = async (requesterId) => {
        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/friends/requests/${requesterId}/decline`, 'PATCH');
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to decline friend request');
            }
            
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error declining friend request');
        }
    };

    // Cancel sent friend request
    const cancelFriendRequest = async (recipientId) => {
        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/friends/requests/${recipientId}`, 'DELETE');
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to cancel friend request');
            }
            
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error cancelling friend request');
        }
    };

    // Remove friend
    const removeFriend = async (friendId) => {
        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/friends/${friendId}`, 'DELETE');
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to remove friend');
            }
            
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error removing friend');
        }
    };

    // Get user's friends list
    const getFriends = async () => {
        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/friends`, 'GET');
            
            if (!response.ok) {
                throw new Error('Failed to fetch friends');
            }
            
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching friends');
        }
    };

    // Get sent friend requests
    const getSentRequests = async () => {
        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/friends/requests/sent`, 'GET');
            
            if (!response.ok) {
                throw new Error('Failed to fetch sent requests');
            }
            
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching sent requests');
        }
    };

    // Get received friend requests
    const getReceivedRequests = async () => {
        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/friends/requests/received`, 'GET');
            
            if (!response.ok) {
                throw new Error('Failed to fetch received requests');
            }
            
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching received requests');
        }
    };

    // Get friends for a specific user by userNumber
    const getFriendsByUserNumber = async (userNumber) => {
        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/friends/user/${userNumber}`, 'GET');
            
            if (!response.ok) {
                if (response.status === 404) {
                    throw new Error('User not found');
                }
                throw new Error('Failed to fetch user friends');
            }
            
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching user friends');
        }
    };

    return {
        // Status checking
        checkFriendshipStatus,
        
        // Friend requests
        sendFriendRequest,
        acceptFriendRequest,
        declineFriendRequest,
        cancelFriendRequest,
        
        // Friend management
        removeFriend,
        
        // Data fetching
        getFriends,
        getFriendsByUserNumber,
        getSentRequests,
        getReceivedRequests
    };
};
